/**
 * ZodiacCarousel12 — 12-sign carousel for zodiac_collection.
 * design-pending-review: tokens-only extrapolation from Calestea DS until
 * a concrete Zodiac artboard ships.
 *
 * Each slide renders Grid3x3 mini-grid (9 frames per sign).
 * Zip CTA renders at bottom when `result.zipDownloadUrl` present.
 */

import { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { useTranslation } from 'react-i18next';
import type { AvatarResult, ZodiacSign } from '../../../types/avatarFlow';
import { Grid3x3 } from './Grid3x3';
import styles from './ZodiacCarousel12.module.css';

const SIGNS: ZodiacSign[] = [
  'aries', 'taurus', 'gemini', 'cancer', 'leo', 'virgo',
  'libra', 'scorpio', 'sagittarius', 'capricorn', 'aquarius', 'pisces',
];

export interface ZodiacCarousel12Props {
  result: AvatarResult;
  onSelectFrame?: (index: number) => void;
  onSignChange?: (sign: ZodiacSign) => void;
  testId?: string;
}

export function ZodiacCarousel12({
  result,
  onSelectFrame = () => {},
  onSignChange,
  testId = 'result-grid-zodiac-carousel',
}: ZodiacCarousel12Props): JSX.Element {
  const { t } = useTranslation('flows');
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false });
  const [selectedIdx, setSelectedIdx] = useState(0);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = (): void => {
      const idx = emblaApi.selectedScrollSnap();
      setSelectedIdx(idx);
      const sign = SIGNS[idx];
      if (sign && onSignChange) onSignChange(sign);
    };
    emblaApi.on('select', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, onSignChange]);

  const scrollTo = useCallback(
    (idx: number) => emblaApi?.scrollTo(idx),
    [emblaApi],
  );

  // 12 signs × 9 frames = 108. Group frames into 9-frame buckets per sign.
  const slides = SIGNS.map((sign, i) => ({
    sign,
    frames: result.frames.slice(i * 9, (i + 1) * 9),
  }));

  return (
    <div className={styles.root} data-testid={testId} aria-label={t('avatarFlow.aria.zodiacSigns')}>
      <div className={styles.viewport} ref={emblaRef}>
        <div className={styles.container}>
          {slides.map(({ sign, frames }) => (
            <div className={styles.slide} key={sign} data-testid={`zodiac-slide-${sign}`}>
              <h3 className={styles.signTitle}>
                {t(`zodiac.sign.${sign}`, { defaultValue: sign })}
              </h3>
              <Grid3x3 frames={frames} onSelectFrame={onSelectFrame} testId={`zodiac-grid-${sign}`} />
            </div>
          ))}
        </div>
      </div>

      <div className={styles.nav} role="tablist" aria-label={t('avatarFlow.aria.zodiacSigns')}>
        {SIGNS.map((sign, i) => (
          <button
            key={sign}
            type="button"
            className={`${styles.dot} ${selectedIdx === i ? styles.dotActive : ''}`}
            aria-label={t(`zodiac.sign.${sign}`, { defaultValue: sign })}
            aria-selected={selectedIdx === i}
            onClick={() => scrollTo(i)}
            data-testid={`zodiac-nav-${sign}`}
          />
        ))}
      </div>

      {result.zipDownloadUrl && (
        <div className={styles.zipBar}>
          <a
            href={result.zipDownloadUrl}
            className={styles.zipBtn}
            data-testid="zodiac-zip-cta"
            download
          >
            {t('avatarFlow.zodiac.zipCta', { defaultValue: 'Download all (.zip)' })}
          </a>
        </div>
      )}
    </div>
  );
}
